import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import MapPicker from "../../components/MapPicker";
import { useCompany } from "../../contexts/CompanyContext";import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { toast } from "sonner";
import {
  Trash2, Plus, MapPin, ArrowLeft, Home, Building2, Briefcase, Store, Tag, Pencil, ChevronRight, AlertTriangle, ShoppingBag,
  Flame, Droplets, ArrowRight,
} from "lucide-react";
import { pointInAnyPolygon } from "../../lib/geo";

// Supported address types with default label + icon + description. The
// backend stores `type` as a free-form string so we can extend this list
// without a migration.
const ADDRESS_TYPES = [
  { id: "casa",        label: "Casa",        description: "Casa unifamiliar o multifamiliar",  icon: Home },
  { id: "departamento",label: "Departamento",description: "Edificio residencial de varias unidades", icon: Building2 },
  { id: "oficina",     label: "Oficina",     description: "Lugar de trabajo con acceso restringido", icon: Briefcase },
  { id: "negocio",     label: "Negocio",     description: "Local comercial o restaurante",    icon: Store },
  { id: "otro",        label: "Otro",        description: "Hospital, parque, exterior, etc.", icon: Tag },
];
const getType = (id) => ADDRESS_TYPES.find((t) => t.id === id) || ADDRESS_TYPES[0];

export default function Addresses() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentCompany, companies, setCurrentCompany } = useCompany();
  const fallbackCenter = currentCompany?.supply_lat
    ? { lat: currentCompany.supply_lat, lng: currentCompany.supply_lng }
    : null;
  const returnTo = params.get("return");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // address object being edited, null = create
  const [type, setType] = useState("casa");
  const [label, setLabel] = useState("");
  const [reference, setReference] = useState("");
  const [pin, setPin] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // BottomSheet "Pedir aquí" — abre el selector de tipo de producto
  // (Gas / Cilindros / Estaciones) ANTES de mandar al wizard. Sólo se
  // dispara desde la lista de direcciones (no aplica al modo `?return=`
  // donde el cliente vuelve al wizard ya iniciado).
  const [orderHereTarget, setOrderHereTarget] = useState(null);

  const load = () => api.get("/addresses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setType("casa");
    setLabel("");
    setReference("");
    setPin(null);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setType(a.type || "casa");
    // If the stored label equals the default for its type, clear it so the
    // user can type a custom one without having to delete the default first.
    const def = getType(a.type || "casa").label;
    setLabel(a.label && a.label !== def ? a.label : "");
    setReference(a.reference || "");
    setPin({ full_address: a.full_address, lat: a.lat, lng: a.lng });
    setOpen(true);
  };

  const goBack = () => {
    if (returnTo) navigate(returnTo);
    else navigate(-1);
  };

  const save = async () => {
    if (!pin?.full_address) {
      toast.error("Selecciona una dirección en el mapa");
      return;
    }
    const finalLabel = (label || "").trim() || getType(type).label;
    const body = {
      label: finalLabel,
      full_address: pin.full_address,
      lat: pin.lat,
      lng: pin.lng,
      reference,
      type,
    };
    try {
      const { data } = editing
        ? await api.put(`/addresses/${editing.address_id}`, body)
        : await api.post("/addresses", body);
      setOpen(false);
      setEditing(null);
      setLabel(""); setReference(""); setPin(null); setType("casa");
      toast.success(editing ? "Dirección actualizada" : "Dirección guardada");
      if (!editing && returnTo) {
        try { localStorage.setItem("selectedAddressId", data.address_id); } catch (_e) { /* ignore */ }
        navigate(returnTo);
        return;
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/addresses/${deleteTarget.address_id}`);
      toast.success("Dirección eliminada");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        {returnTo && (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-black mb-3"
            data-testid="back-to-order-btn"
          >
            <ArrowLeft size={14} /> Volver al pedido
          </button>
        )}
        <div className="flex items-center justify-between">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Mis direcciones</h1>
          <Button
            onClick={() => { openCreate(); setOpen(true); }}
            className="h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl tap-fade"
            data-testid="add-address-btn"
          >
            <Plus size={16} className="mr-2" />Agregar
          </Button>
        </div>

        <BottomSheet
          open={open}
          onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}
          title={editing ? "Editar dirección" : "Nueva dirección"}
          description={editing
            ? "Actualiza los datos de tu dirección."
            : "Elige el tipo, selecciona el punto exacto en el mapa y guarda."}
          testid="address-form-sheet"
          onPointerDownOutside={(e) => {
            const target = e.target;
            if (target?.closest?.(".pac-container")) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            const target = e.target;
            if (target?.closest?.(".pac-container")) e.preventDefault();
          }}
        >
          <div className="flex flex-col max-h-[72vh] sm:max-h-[74vh]">
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
              <div className="md:grid md:grid-cols-[280px_1fr] md:gap-5 mt-2 flex flex-col gap-4">
                {/* Mobile: map first */}
                <div className="order-1 md:order-2 sheet-col-right">
                  <MapPicker
                    value={pin}
                    onChange={setPin}
                    fallbackCenter={fallbackCenter}
                    circleColor={currentCompany?.primary_color || "#0A0A0A"}
                    serviceAreaPolygons={currentCompany?.service_area_polygons || null}
                  />
                  {/* Aviso de cobertura: si la empresa tiene polígonos
                      definidos y el pin del usuario cae fuera, mostramos
                      una advertencia (no bloqueante: el cliente puede
                      guardar la dirección de todos modos, pero no podrá
                      hacer pedidos a ese punto). */}
                  {pin?.lat != null && pin?.lng != null
                    && (currentCompany?.service_area_polygons || []).length > 0
                    && !pointInAnyPolygon(pin.lat, pin.lng, currentCompany.service_area_polygons) && (() => {
                      // Sugerencias: empresas distintas a la activa cuyos polígonos
                      // sí cubren este punto. Útil cuando el cliente pidió a una
                      // empresa equivocada y hay otra en la zona.
                      const matches = (companies || []).filter((c) =>
                        c.company_id !== currentCompany.company_id
                        && (c.service_area_polygons || []).length > 0
                        && pointInAnyPolygon(pin.lat, pin.lng, c.service_area_polygons)
                      );
                      return (
                    <div
                      className="mt-3 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900"
                      data-testid="address-out-of-coverage-warning"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                        <div className="text-xs leading-snug">
                          <p className="font-semibold text-sm">Fuera del área de cobertura</p>
                          <p>
                            Esta dirección queda fuera de la zona de servicio de
                            {" "}<span className="font-semibold">{currentCompany.name}</span>.
                            Puedes guardarla, pero no podrás hacer pedidos a este
                            punto hasta que la empresa amplíe su cobertura.
                          </p>
                        </div>
                      </div>
                      {matches.length > 0 && (
                        <div className="border-t border-amber-200 pt-2 mt-1">
                          <p className="text-xs text-amber-900 mb-1.5 flex items-center gap-1.5">
                            <Store size={12} />
                            <span>
                              {matches.length === 1
                                ? "Una empresa sí entrega aquí:"
                                : `${matches.length} empresas sí entregan aquí:`}
                            </span>
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {matches.map((c) => (
                              <button
                                key={c.company_id}
                                type="button"
                                onClick={() => {
                                  setCurrentCompany(c);
                                  toast.success(`Empresa cambiada a ${c.name}`);
                                }}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 transition"
                                data-testid={`switch-to-company-${c.company_id}`}
                              >
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ background: c.primary_color || "#0A0A0A" }}
                                />
                                Cambiar a {c.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                      );
                    })()}
                </div>
                <div className="order-2 md:order-1 space-y-4 sheet-col-left">
                  <div>
                    <Label className="text-sm font-semibold">Tipo de lugar</Label>
                    <p className="text-xs text-neutral-500 mb-2">
                      Cuéntanos el tipo para entregas más precisas.
                    </p>
                    <div className="space-y-2" role="radiogroup" data-testid="address-type-list">
                      {ADDRESS_TYPES.map((t) => {
                        const Icon = t.icon;
                        const active = type === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setType(t.id)}
                            data-testid={`address-type-${t.id}`}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all tap-fade ${
                              active
                                ? "border-[color:var(--company-primary)] bg-[color:var(--company-primary)]/5 shadow-sm"
                                : "border-neutral-200 bg-white hover:border-neutral-300"
                            }`}
                          >
                            <div
                              className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                                active ? "bg-company text-[color:var(--company-primary-contrast)]" : "bg-neutral-100 text-neutral-700"
                              }`}
                            >
                              <Icon size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm leading-tight">{t.label}</p>
                              <p className="text-xs text-neutral-500 line-clamp-1">{t.description}</p>
                            </div>
                            <ChevronRight
                              size={16}
                              className={active ? "text-[color:var(--company-primary)]" : "text-neutral-300"}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Etiqueta personalizada (opcional)</Label>
                    <Input
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder={`Ej. ${getType(type).label} de mi mamá`}
                      className="h-11 mt-1"
                      data-testid="address-label-input"
                    />
                    <p className="text-[11px] text-neutral-400 mt-1">
                      Si lo dejas vacío, usaremos "{getType(type).label}".
                    </p>
                  </div>
                  <div>
                    <Label>Referencia (opcional)</Label>
                    <Input
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Timbre, piso, color puerta..."
                      className="h-11 mt-1"
                      data-testid="address-reference-input"
                    />
                  </div>
                  <Button
                    onClick={save}
                    style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                    className="w-full h-11 hover:opacity-90 rounded-xl tap-fade md:hidden"
                    data-testid="save-address-btn"
                  >
                    {editing ? "Guardar cambios" : "Guardar"}
                  </Button>
                </div>
              </div>
            </div>
            {/* Desktop: sticky footer save button, always visible regardless of
                scroll position. Mobile keeps its own button inside the form.
                `shrink-0` prevents flex from compressing the footer when the
                body content is tall, otherwise the button gets clipped. */}
            <div className="hidden md:block shrink-0 px-6 py-4 border-t border-neutral-100 bg-white">
              <Button
                onClick={save}
                style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                className="w-full h-12 hover:opacity-90 rounded-xl tap-fade flex items-center justify-center font-semibold"
                data-testid="save-address-btn-desktop"
              >
                {editing ? "Guardar cambios" : "Guardar"}
              </Button>
            </div>
          </div>
        </BottomSheet>

        <div className="mt-6 space-y-3">
          {list.length === 0 ? (
            <p className="text-neutral-500 text-sm">Aún no tienes direcciones guardadas.</p>
          ) : list.map((a) => {
            const tdef = getType(a.type);
            const Icon = tdef.icon;
            const polys = currentCompany?.service_area_polygons || [];
            const hasCoords = a.lat != null && a.lng != null;
            // ¿Está fuera de la cobertura de la empresa actual?
            const outOfCoverage = polys.length > 0 && hasCoords
              && !pointInAnyPolygon(a.lat, a.lng, polys);
            // Empresas alternativas (excluyendo la activa) que también
            // cubren este punto. SÓLO se muestran cuando la dirección
            // está fuera de cobertura de la empresa activa — sirven
            // como salida de emergencia, no como comparador permanente.
            const altCompanies = (outOfCoverage && hasCoords)
              ? (companies || []).filter((c) =>
                  c.company_id !== currentCompany?.company_id
                  && (c.service_area_polygons || []).length > 0
                  && pointInAnyPolygon(a.lat, a.lng, c.service_area_polygons)
                )
              : [];
            const altLabel = "Sí entrega aquí:";
            return (
              <div
                key={a.address_id}
                className={`bg-white border rounded-xl p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3 overflow-hidden ${
                  outOfCoverage ? "border-red-200" : "border-neutral-200"
                }`}
                data-testid={`address-item-${a.address_id}`}
              >
                <div className="flex items-start gap-3 min-w-0 w-full md:flex-1">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                    outOfCoverage ? "bg-red-50 text-red-700" : "bg-company/10 text-company"
                  }`}>
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="truncate">{a.label}</span>
                      <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 font-semibold">
                        {tdef.label}
                      </span>
                      {outOfCoverage && (
                        <span
                          className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold border border-red-200"
                          data-testid={`address-coverage-chip-${a.address_id}`}
                        >
                          Fuera de cobertura
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-neutral-500 truncate">{a.full_address}</p>
                    {a.reference && (
                      <p className="text-xs text-neutral-400 mt-0.5 truncate">{a.reference}</p>
                    )}
                    {/* Marketplace implícito — qué OTRAS empresas sí entregan
                        a esta dirección, con un atajo para cambiarlas. */}
                    {altCompanies.length > 0 && (
                      <div
                        className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-neutral-600"
                        data-testid={`address-alt-companies-${a.address_id}`}
                      >
                        <span className="text-neutral-500">{altLabel}</span>
                        {altCompanies.map((c) => (
                          <button
                            key={c.company_id}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCurrentCompany(c);
                              try { localStorage.setItem("selected_company_id", c.company_id); } catch (_err) {}
                              toast.success(`Empresa cambiada a ${c.name}`);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-50 border border-neutral-200 hover:border-neutral-400 hover:bg-white transition font-semibold"
                            data-testid={`address-switch-company-${a.address_id}-${c.company_id}`}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ background: c.primary_color || "#0A0A0A" }}
                            />
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Acciones — en mobile: full-width en una fila debajo,
                    "Pedir aquí" como CTA primario que ocupa el ancho y
                    los iconos editar/borrar a la derecha en su propia
                    fila pequeña. En desktop: todo inline al lado derecho. */}
                <div className="flex items-center gap-2 w-full md:w-auto md:shrink-0 md:self-auto">
                  {returnTo && (
                    <Button
                      size="sm"
                      style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                      className="h-10 hover:opacity-90 tap-fade flex-1 md:flex-none"
                      onClick={() => {
                        try { localStorage.setItem("selectedAddressId", a.address_id); } catch (_e) { /* ignore */ }
                        const sep = returnTo.includes("?") ? "&" : "?";
                        navigate(`${returnTo}${sep}addr=${encodeURIComponent(a.address_id)}`);
                      }}
                      data-testid={`use-address-${a.address_id}`}
                    >
                      Usar
                    </Button>
                  )}
                  {/* Atajo "Pedir aquí" — abre BottomSheet de selección
                      de tipo de producto antes de mandar al wizard. La
                      conmutación de empresa al wizard se preserva via
                      el query param `addr` (preferred_company_id). */}
                  {!returnTo && hasCoords && (
                    <Button
                      size="sm"
                      style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                      className="h-10 hover:opacity-90 tap-fade flex-1 md:flex-none"
                      onClick={() => setOrderHereTarget(a)}
                      data-testid={`order-here-${a.address_id}`}
                    >
                      <ShoppingBag size={14} className="mr-1.5" /> Pedir aquí
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(a)}
                    data-testid={`edit-address-${a.address_id}`}
                    aria-label="Editar dirección"
                    className="tap-fade h-10 w-10 shrink-0"
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteTarget(a)}
                    data-testid={`delete-address-${a.address_id}`}
                    aria-label="Eliminar dirección"
                    className="tap-fade hover:text-red-600 hover:bg-red-50 h-10 w-10 shrink-0"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* BottomSheet "Pedir aquí" — selector de tipo de producto.
            Sólo se abre desde la lista de direcciones (no en modo
            ?return=). El click en un kind navega al wizard con `addr`
            + `kind` para que el catálogo cargue ya filtrado. */}
        <BottomSheet
          open={!!orderHereTarget}
          onOpenChange={(o) => { if (!o) setOrderHereTarget(null); }}
          title="¿Qué vas a pedir?"
          description={orderHereTarget ? `Entrega en ${orderHereTarget.label} · ${orderHereTarget.full_address}` : ""}
          testid="order-here-sheet"
        >
          <div className="px-6 pb-6">
            <div className="mt-2 grid grid-cols-2 gap-3 max-w-2xl mx-auto">
              <button
                type="button"
                onClick={() => {
                  const id = orderHereTarget.address_id;
                  setOrderHereTarget(null);
                  navigate(`/app/order/new?kind=bulk&addr=${encodeURIComponent(id)}&fresh=1`);
                }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="order-here-choose-bulk"
              >
                <Droplets size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Gas</p>
                <p className="text-xs text-neutral-500 mt-0.5">Litros o kilogramos</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = orderHereTarget.address_id;
                  setOrderHereTarget(null);
                  navigate(`/app/order/new?kind=cylinder&addr=${encodeURIComponent(id)}&fresh=1`);
                }}
                className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                data-testid="order-here-choose-cylinder"
              >
                <Flame size={26} className="text-company" />
                <p className="mt-3 font-display font-extrabold text-xl">Cilindros</p>
                <p className="text-xs text-neutral-500 mt-0.5">20 / 35 / 45 kg</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                  Elegir <ArrowRight size={14} />
                </span>
              </button>
            </div>
          </div>
        </BottomSheet>

        <BottomSheet
          open={!!deleteTarget}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          testid="delete-address-confirm"
        >
          <div className="px-6 pt-0 pb-6">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                <Trash2 size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-extrabold leading-tight">¿Eliminar esta dirección?</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  {deleteTarget ? (
                    <>
                      Vas a eliminar <span className="font-semibold">{deleteTarget.label}</span>
                      {" — "}
                      <span className="text-neutral-500">{deleteTarget.full_address}</span>.
                      Esta acción no se puede deshacer.
                    </>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                className="h-11 rounded-xl flex-1 tap-fade"
                data-testid="delete-address-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDelete}
                data-testid="delete-address-confirm-btn"
                className="h-11 rounded-xl flex-1 bg-red-600 hover:bg-red-700 text-white tap-fade"
              >
                <Trash2 size={16} className="mr-2" />
                Eliminar
              </Button>
            </div>
          </div>
        </BottomSheet>
      </main>
    </div>
  );
}
